/**
 * Lending Workflow Page
 *
 * Displays loan application stages using the TimelineChart component.
 * Shows active loan workflows with stage progression:
 * Application -> Valuation -> Underwriting -> Approval -> Funding -> Servicing.
 */
import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { advisorClient } from '../api/client';
import { AdvisorServiceMissingError, AdvisorServiceNotice, advisorFailure } from '../components/AdvisorServiceNotice';
import type { components } from '@aaimplatform/institutional-types/advisor-tools';
import type { ColumnDef, SelectOption, TimelineStage } from '@aaimplatform/ui';
import {
  Heading,
  BodyText,
  Card,
  Button,
  MetricCard,
  DashboardGrid,
  DashboardWidget,
  DataGrid,
  TimelineChart,
  Select,
} from '@aaimplatform/ui';


type LoanApplication = components['schemas']['LoanApplication'];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const WORKFLOW_STAGES = [
  'application',
  'valuation',
  'underwriting',
  'approval',
  'funding',
  'servicing',
] as const;

const STAGE_LABELS: Record<string, string> = {
  application: 'Application',
  valuation: 'Valuation',
  underwriting: 'Underwriting',
  approval: 'Approval',
  funding: 'Funding',
  servicing: 'Servicing',
};

const STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'rejected', label: 'Rejected' },
];

const STATUS_BADGE_STYLES: Record<string, string> = {
  in_progress: 'bg-accent/10 text-accent',
  completed: 'bg-growth/10 text-growth',
  on_hold: 'bg-warning/10 text-warning',
  rejected: 'bg-error/10 text-error',
  pending: 'bg-recessed text-secondary',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Build TimelineStage array from a loan application's current stage.
 */
function buildTimelineStages(currentStage: string, loanStatus: string): TimelineStage[] {
  const currentIndex = WORKFLOW_STAGES.indexOf(currentStage as typeof WORKFLOW_STAGES[number]);

  return WORKFLOW_STAGES.map((stage, index) => {
    let status: TimelineStage['status'];
    if (loanStatus === 'rejected') {
      status = index <= currentIndex ? 'completed' : 'skipped';
      if (index === currentIndex) status = 'overdue';
    } else if (loanStatus === 'on_hold') {
      status = index < currentIndex ? 'completed' : index === currentIndex ? 'active' : 'pending';
    } else if (index < currentIndex) {
      status = 'completed';
    } else if (index === currentIndex) {
      status = loanStatus === 'completed' ? 'completed' : 'active';
    } else {
      status = 'pending';
    }

    return {
      id: stage,
      label: STAGE_LABELS[stage],
      status,
    };
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LendingWorkflowPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);

  const { data: loans, isLoading, error } = useQuery({
    queryKey: ['lending-workflows', statusFilter],
    queryFn: async (): Promise<LoanApplication[]> => {
      const { data, error, response } = await advisorClient.GET('/api/lending/workflows', {
        params: {
          query: {
            ...(statusFilter ? { status: statusFilter } : {}),
          },
        },
      });
      if (error || !response.ok) throw advisorFailure(response, error, 'Failed to load lending workflows');
      return (data ?? []) as LoanApplication[];
    },
  });

  // Summary metrics
  const metrics = useMemo(() => {
    if (!loans || loans.length === 0) {
      return { total: 0, inProgress: 0, completed: 0, totalVolume: 0 };
    }
    return {
      total: loans.length,
      inProgress: loans.filter((l) => l.status === 'in_progress').length,
      completed: loans.filter((l) => l.status === 'completed').length,
      totalVolume: loans.reduce((sum, l) => sum + l.loanAmount, 0),
    };
  }, [loans]);

  const selectedLoan = useMemo(
    () => loans?.find((l: LoanApplication) => l.id === selectedLoanId) ?? null,
    [loans, selectedLoanId],
  );

  const columns = useMemo<ColumnDef<LoanApplication, unknown>[]>(() => [
    {
      id: 'clientName',
      header: 'Client',
      accessorKey: 'clientName',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="font-semibold text-primary">{String(getValue())}</span>
      ),
    },
    {
      id: 'loanNumber',
      header: 'Loan #',
      accessorKey: 'loanNumber',
      size: 120,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="font-mono text-primary">{String(getValue())}</span>
      ),
    },
    {
      id: 'loanAmount',
      header: 'Amount',
      accessorKey: 'loanAmount',
      size: 140,
      enableSorting: true,
      meta: { align: 'right' },
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="font-mono tabular-nums">{formatCurrency(Number(getValue()))}</span>
      ),
    },
    {
      id: 'currentStage',
      header: 'Current Stage',
      accessorKey: 'currentStage',
      size: 140,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => (
        <span className="text-primary">{STAGE_LABELS[String(getValue())] ?? String(getValue())}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 120,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => {
        const status = String(getValue());
        return (
          <span
            className={`inline-block px-2 py-0.5 rounded text-xs font-semibold capitalize ${
              STATUS_BADGE_STYLES[status] ?? 'bg-recessed text-secondary'
            }`}
          >
            {status.replace(/_/g, ' ')}
          </span>
        );
      },
    },
    {
      id: 'submittedDate',
      header: 'Submitted',
      accessorKey: 'submittedDate',
      size: 120,
      enableSorting: true,
      cell: ({ getValue }: { getValue: () => unknown }) => formatDate(String(getValue())),
    },
  ], []);

  const handleRowClick = useCallback((row: LoanApplication) => {
    setSelectedLoanId((prev) => (prev === row.id ? null : row.id));
  }, []);

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <BodyText className="text-secondary mt-2">
            Track loan applications through origination stages
          </BodyText>
        </div>
        <Button variant="primary" onClick={() => navigate('/proposals/new')}>
          Start New Application
        </Button>
      </div>

      {/* Summary Metrics */}
      <DashboardGrid columns={4} gap="compact" className="mb-6">
        <DashboardWidget id="total-workflows">
          <MetricCard label="Total Workflows" value={String(metrics.total)} valueColor="primary" />
        </DashboardWidget>
        <DashboardWidget id="in-progress">
          <MetricCard label="In Progress" value={String(metrics.inProgress)} valueColor="accent" />
        </DashboardWidget>
        <DashboardWidget id="completed">
          <MetricCard label="Completed" value={String(metrics.completed)} valueColor="growth" />
        </DashboardWidget>
        <DashboardWidget id="total-volume">
          <MetricCard label="Pipeline Volume" value={formatCurrency(metrics.totalVolume)} valueColor="primary" />
        </DashboardWidget>
      </DashboardGrid>

      {/* Filter */}
      <Card className="mb-6">
        <div className="max-w-xs">
          <Select
            label="Filter by Status"
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setSelectedLoanId(null);
            }}
          />
        </div>
      </Card>

      {/* Workflow Grid */}
      {error instanceof AdvisorServiceMissingError ? (
        <AdvisorServiceNotice />
      ) : error ? (
        <Card>
          <div className="p-4 bg-error/10 border border-error/20 rounded">
            <BodyText className="text-error font-semibold">Error Loading Workflows</BodyText>
            <BodyText className="text-error mt-1">
              Failed to retrieve lending workflows. Verify the orchestration engine is available.
            </BodyText>
          </div>
        </Card>
      ) : (
        <DataGrid
          data={(loans ?? []) as LoanApplication[]}
          columns={columns}
          isLoading={isLoading}
          enableGlobalFilter
          enableColumnFilters
          enablePagination
          defaultPageSize={15}
          pageSizeOptions={[10, 15, 25, 50]}
          striped
          hoverable
          density="default"
          onRowClick={handleRowClick}
          getRowId={(row) => row.id}
          emptyMessage="No lending workflows found."
        />
      )}

      {/* Selected Loan Timeline */}
      {selectedLoan && (
        <Card className="mt-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
            <div>
              <Heading level={3} as={3} size="card">
                {selectedLoan.clientName} -- Loan #{selectedLoan.loanNumber}
              </Heading>
              <BodyText className="text-secondary mt-1">
                {formatCurrency(selectedLoan.loanAmount)} | Submitted {formatDate(selectedLoan.submittedDate)}
              </BodyText>
            </div>
            <Button variant="secondary" onClick={() => setSelectedLoanId(null)}>
              Close
            </Button>
          </div>

          <TimelineChart
            title="Loan Origination Progress"
            subtitle={`Current stage: ${STAGE_LABELS[selectedLoan.currentStage] ?? selectedLoan.currentStage}`}
            stages={buildTimelineStages(selectedLoan.currentStage, selectedLoan.status)}
            orientation="horizontal"
            showDurations={false}
          />

          {/* Notes section reserved for future schema expansion */}
        </Card>
      )}
    </div>
  );
}
